import { Link } from "react-router-dom";
import type { Anime } from "@/types";
import { useFavorites } from "@/hooks/useFavorites";

interface AnimeCardProps {
  anime: Anime;
  showFavorite?: boolean;
}

export default function AnimeCard({ anime, showFavorite = true }: AnimeCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(anime.id);

  return (
    <div className="group relative bg-background-50 rounded-lg overflow-hidden border border-background-200/80 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1">
      <Link to={`/detail/${anime.id}`} className="block">
        <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-200">
          <img
            src={anime.cover}
            alt={`${anime.title} - 动漫封面`}
            title={`${anime.title} - 动漫全集`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-2.5">
            <span className="px-2 py-0.5 rounded-full bg-foreground-950/70 text-background-50 text-[11px] font-medium backdrop-blur-sm">
              {anime.status}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold">
              {anime.rating.toFixed(1)}
            </span>
          </div>
          <div className="absolute inset-x-0 bottom-0 p-2.5 bg-gradient-to-t from-foreground-950/85 to-transparent">
            <p className="text-background-50 text-[11px]">
              更新至第 {anime.latestEpisode} 集 / 共 {anime.episodes} 集
            </p>
          </div>
        </div>
      </Link>

      {showFavorite && (
        <button
          onClick={() => toggleFavorite(anime.id)}
          aria-label={favorited ? "取消收藏" : "收藏"}
          className={`absolute top-2.5 right-2.5 w-8 h-8 mt-7 flex items-center justify-center rounded-full backdrop-blur-sm transition-colors cursor-pointer ${
            favorited
              ? "bg-primary-500 text-background-50"
              : "bg-foreground-950/50 text-background-50 hover:bg-foreground-950/70"
          }`}
        >
          <i className={favorited ? "ri-heart-3-fill text-sm" : "ri-heart-3-line text-sm"} />
        </button>
      )}

      <div className="p-3">
        <Link to={`/detail/${anime.id}`}>
          <h3 className="font-heading font-semibold text-sm text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
            {anime.title}
          </h3>
        </Link>
        <p className="text-xs text-foreground-500 mt-1.5 clamp-2 leading-relaxed min-h-[2.4rem]">
          {anime.synopsis}
        </p>
        <div className="flex items-center flex-wrap gap-1.5 mt-2.5">
          <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-[11px]">
            {anime.category}
          </span>
          <span className="px-2 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[11px]">
            {anime.year}
          </span>
          <span className="px-2 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[11px]">
            {anime.region}
          </span>
        </div>
      </div>
    </div>
  );
}